import { apiFetch } from './client.js';

/**
 * Cliente de "cómo salieron las importaciones a OPUS 27" (solo staff Ecosoft).
 * El porqué está en `apps/api/src/routes/staffImportaciones.ts`.
 */

export interface ErrorDeRenglon {
  clave: string;
  mensaje: string;
}

export interface ImportacionReportada {
  id: string;
  fecha: string;
  resultado: 'ok' | 'parcial' | 'fallo';
  empresa: string | null;
  usuario: string | null;
  proyecto: string | null;
  presupuesto: string | null;
  quoteId: string;
  obra: string | null;
  /** Dato crudo, sólo para el detalle: dice `1.0.0.0` en TODA compilación de m1 (el número
   *  está escrito a mano en el AssemblyInfo y nunca se movió). No sirve para decidir nada. */
  versionM1: string | null;
  /** Fecha de compilación del binario, del encabezado del ejecutable. Es lo ÚNICO que
   *  distingue una compilación de otra. */
  compiladoM1: string | null;
  /** ¿Tiene que actualizar? Ya viene contestado y redactado desde el servidor
   *  (`apps/api/src/services/versionDeM1.ts`), para que la respuesta sea una sola. */
  version: {
    estado: 'al_dia' | 'desactualizado' | 'sin_fecha_confiable';
    etiqueta: string;
    leFalta: string[];
  };
  /** Dónde falló: fase, destino, contra qué obra se mapeó cada catálogo. */
  contexto: {
    fase?: 'A' | 'B';
    destino?: 'obra_nueva' | 'obra_existente';
    catalogos?: Array<{ catalogoM0?: string; obraM1?: string; recursosTransferidos?: number }>;
    conceptoEnCurso?: { clave?: string; esSintetico?: boolean };
  };
  conceptosImportados: number;
  conceptosFallidos: number;
  errores: ErrorDeRenglon[];
}

/**
 * SE LO LLEVÓ Y NO VOLVIÓ. La otra mitad de la pregunta: quien se lleva el presupuesto y
 * nunca reporta nada es invisible en la lista de arriba, y ése es el caso peor. El criterio
 * y la medición que fijó los números están en `apps/api/src/services/seLoLlevoYNoVolvio.ts`.
 */
export interface Atasco {
  id: string;
  /** `no_volvio` es el aviso. `insistiendo` todavía puede terminar bien: no se afirma nada. */
  estado: 'no_volvio' | 'insistiendo';
  tenantId: string;
  userId: string | null;
  quoteId: string;
  empresa: string | null;
  usuario: string | null;
  presupuesto: string | null;
  conceptos: number | null;
  descargas: number;
  importaciones: number;
  primera: string;
  ultima: string;
  ventanaMinutos: number;
  silencioMinutos: number;
  /** Ya redactado por el servidor: la pantalla no arma texto. */
  titulo: string;
  detalle: string;
  /** Qué OPUS 27 tiene. `null` mientras su binario no mande el encabezado de identificación
   *  — que hoy es siempre. Sin esto la fila sirve igual; con esto además dice la versión. */
  opus27: {
    version: string;
    compilado: string | null;
    veredicto: { estado: 'al_dia' | 'desactualizado' | 'sin_fecha_confiable'; etiqueta: string; leFalta: string[] };
  } | null;
}

export interface RespuestaImportaciones {
  importaciones: ImportacionReportada[];
  atascos: Atasco[];
  /** Con qué números se decidió quién está atascado. Se muestran para que el criterio se
   *  pueda leer en la pantalla en vez de tener que abrir el código. */
  umbrales: { descargasMin: number; esperaMinutos: number; dias: number };
}

export async function getImportaciones(
  ver: 'fallidas' | 'todas' = 'fallidas',
): Promise<RespuestaImportaciones> {
  const r = await apiFetch<RespuestaImportaciones>(`/api/v1/staff/importaciones?ver=${ver}`);
  return { importaciones: r.importaciones ?? [], atascos: r.atascos ?? [], umbrales: r.umbrales };
}

/** Saca el reporte de la bandeja. Borra SOLO el reporte: el presupuesto del cliente y su
 *  obra en OPUS 27 no se tocan. */
export async function borrarImportacion(id: string): Promise<void> {
  await apiFetch(`/api/v1/staff/importaciones/${id}`, { method: 'DELETE' });
}
